import { NavigationPaths, useClientAIStudio } from './ClientAIStudio.context'
import React, { useMemo } from 'react'

import { BEIGE_500 } from 'constants/styling/theme'
import { MUIBadge } from 'components/common/MUIBadge'
import { MUIButton } from 'components/common/MUIButton'
import Stack from '@mui/material/Stack'
import { useAuth0 } from 'utils/auth'
import { useTranslation } from 'react-i18next'

/**
 * @component
 * ClientAIStudioNavTabs renders a navigation bar with tabs specific to Client's AI Studio.
 * 
 * @example
 *  <ClientAIStudioNavTabs />
 */
export const ClientAIStudioNavTabs: React.FC = () => {
  const { roles } = useAuth0()

  const { t } = useTranslation(['ai_studio_editing', 'common'])

  const { activeNavTab } = useClientAIStudio()

  const tabs = useMemo(() => {
    return Object.values(NavigationPaths).map((tab) => ({
      label: tab,
      tab,
      badge: tab === NavigationPaths.VIRTUAL_STAGING,
    }))
  }, [])

  if (!roles.isClient) return <></>

  return (
    <Stack
      flex={1}
      justifyContent="center"
      direction="row"
      gap={1.2}
      sx={{
        backgroundColor: BEIGE_500,
        padding: .4,
        borderRadius: .8,
        maxWidth: 'fit-content',
        margin: '0 auto',
      }}
    >
      {tabs.map(({ tab, label, badge }) => (
        <MUIButton
          key={tab}
          onClick={() => undefined} // TODO: Use setActiveNavTab callback from "useClientAIStudio" when Virtual staging is ready
          type="secondaryBorder"
          borderStyle="rounded-xs"
          sx={{
            backgroundColor: activeNavTab === tab ? 'white' : 'rgba(0,0,0,0)',
            color: activeNavTab === tab ? 'black' : 'inherit',
            border: 'none',
            padding: '8px 16px',
            borderRadius: '4px',
            cursor: tab === NavigationPaths.VISUAL_EDITING ? 'pointer' : 'not-allowed', // Remove condition when virtual staging is ready
            fontWeight: activeNavTab === tab ? '600' : '500',
            '&:hover': {
              color: 'black',
            },
          }}
        >
          {t(`navigation_tab.${label}`)}
          {badge &&
            <Stack marginX='auto'>
              <MUIBadge label={t('common:coming_soon')} size="xs" color="coral" />
            </Stack>
          }
        </MUIButton>
      ))}
    </Stack>
  )
}
